import { useEffect, useState } from 'react';
// import "primereact/resources/themes/saga-blue/theme.css";
import {
  Tree,
  TreeCheckboxSelectionKeys,
  TreeMultipleSelectionKeys,
  TreeTogglerTemplateOptions,
} from 'primereact/tree';
import { Button } from 'primereact/button';
import { TreeNode } from 'primereact/treenode';
import { classNames } from 'primereact/utils';
import {
  endpointSharedDocumentGDriveGET,
  endpointUploadDocsFromGDriveAndFieldExtractionPOST,
  GDriveSharedDocumentAPIModel,
} from '~/api/def/endpoints/document/documentGDriveEndpoint';
import { Toast } from 'primereact/toast';
import {
  endpointFundGooglWatchChannelPOST,
  endpointFundPcGooglWatchChannelSyncPUT,
} from '~/api/def/endpoints/fundEndpoint';
import MessageComponent from '~/components/ui/misc/MessageComponent';
import { Checkbox } from 'primereact/checkbox';
import { endpointPcGoogleWatchChannelPUT } from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { useAtom } from 'jotai';
import { toastAtom } from '~/atoms/toastAtom';

type GoogleDriveUploaderProps = {
  fundId: string;
  portfolioCompanyId?: string;
  onSubmitComplete?: () => void;
  onUploadInit?: () => void;
  showPcFolderMapping?: boolean;
  isGDriveSyncMode?: boolean;
  isPcFolderMapped?: boolean;
  refetchFund?: () => void;
};

export const GoogleDriveUploader = ({
  fundId,
  portfolioCompanyId,
  onSubmitComplete,
  onUploadInit,
  showPcFolderMapping = false,
  isGDriveSyncMode = false,
  isPcFolderMapped = false,
  refetchFund,
}: GoogleDriveUploaderProps) => {
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const [sharedFiles, setSharedFiles] = useState<
    GDriveSharedDocumentAPIModel[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [folderPcMapping, setFolderPcMapping] =
    useState<boolean>(isPcFolderMapped);
  const [folderPcMappingLoading, setFolderPcMappingLoading] =
    useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<
    null | string | TreeMultipleSelectionKeys | TreeCheckboxSelectionKeys
  >(null);
  const [documentIds, setDocumentIds] = useState<string[]>([]);
  useEffect(() => {
    setIsLoading(true);
    endpointSharedDocumentGDriveGET(
      isGDriveSyncMode
        ? {
            filterSyncedFolders: true,
            fundId: fundId,
            portfolioCompanyId:
              isGDriveSyncMode && portfolioCompanyId
                ? portfolioCompanyId
                : undefined,
          }
        : undefined,
    )
      .then((res) => {
        setSharedFiles(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setIsLoading(false));
  }, [fundId, isGDriveSyncMode, portfolioCompanyId]);

  const getNodeByKey = (keyT: string) => {
    const findNode = (
      nodes: GDriveSharedDocumentAPIModel[],
      key: string,
    ): GDriveSharedDocumentAPIModel | null => {
      for (const node of nodes) {
        if (node.key === key) {
          return node;
        }
        if (node.children) {
          const foundNode = findNode(node.children, key);
          if (foundNode) return foundNode;
        }
      }
      return null;
    };
    return findNode(sharedFiles, keyT);
  };

  const handleUploadDocsAndRunFieldExtraction = (documentIds: string[]) => {
    setSubmittingForm(true);
    onUploadInit && onUploadInit();
    endpointUploadDocsFromGDriveAndFieldExtractionPOST({
      fundId: fundId,
      documentIds: documentIds,
      portfolioCompanyId: portfolioCompanyId || undefined,
    })
      .then((res) => {
        if (res && res.response === 200) {
          toastCurrent?.show({
            severity: 'success',
            detail:
              'Files uploaded successfully and extraction has been started.',
          });
          onSubmitComplete && onSubmitComplete();
        } else if (res) {
          toastCurrent?.show({
            severity: 'success',
            detail:
              'Files uploaded successfully and extraction has been started.',
          });
        } else {
          toastCurrent?.show({
            severity: 'error',
            detail: 'Something went wrong.',
          });
        }
      })
      .catch(() => {
        toastCurrent?.show({
          severity: 'error',
          detail: 'Something went wrong.',
        });
      })
      .finally(() => {
        setSubmittingForm(false);
      });
  };

  const toggleFundPcGoogleDriveSync = () => {
    setFolderPcMappingLoading(true);
    endpointFundPcGooglWatchChannelSyncPUT(fundId, {
      pcGDriveFolderSync: folderPcMapping,
    })
      .then((res) => {
        if (res && res.response === 200) {
          toastCurrent?.show({
            severity: 'success',
            detail: 'Portfolio companies folder mapping toggled successfully.',
          });
          refetchFund && refetchFund();
        }
      })
      .catch(() => {
        toastCurrent?.show({
          severity: 'error',
          detail: 'Something went wrong.',
        });
      })
      .finally(() => {
        setFolderPcMappingLoading(false);
      });
  };

  const handleAddFolderGoogleSync = (documentIds: string[]) => {
    setSubmittingForm(true);
    endpointFundGooglWatchChannelPOST({
      folderIds: documentIds,
      fundId: fundId,
    })
      .then((res) => {
        if (res && res.response === 200) {
          toastCurrent?.show({
            severity: 'success',
            detail: 'Sync added successfully.',
          });
          onSubmitComplete && onSubmitComplete();
        } else if (res) {
          toastCurrent?.show({
            severity: 'success',
            detail: 'Sync added successfully.',
          });
        } else {
          toastCurrent?.show({
            severity: 'error',
            detail: 'Something went wrong.',
          });
        }
      })
      .catch(() => {
        toastCurrent?.show({
          severity: 'error',
          detail: 'Something went wrong.',
        });
      })
      .finally(() => {
        setSubmittingForm(false);
      });
  };

  const handlePcFolderGoogleSync = (documentIds: string[]) => {
    setSubmittingForm(true);
    portfolioCompanyId &&
      endpointPcGoogleWatchChannelPUT(portfolioCompanyId, {
        folderIds: documentIds,
        fundId: fundId,
      })
        .then((res) => {
          if (res && res.response === 200) {
            toastCurrent?.show({
              severity: 'success',
              detail: 'Sync added successfully.',
            });
            onSubmitComplete && onSubmitComplete();
          } else {
            toastCurrent?.show({
              severity: 'error',
              detail: 'Something went wrong.',
            });
          }
        })
        .catch(() => {
          toastCurrent?.show({
            severity: 'error',
            detail: 'Something went wrong.',
          });
        })
        .finally(() => {
          setSubmittingForm(false);
        });
  };

  const handleUpload = () => {
    const documentIds = getDataIds(
      selectedKeys as Record<string, boolean>,
      sharedFiles,
      isGDriveSyncMode,
    );
    if (documentIds && documentIds.length) {
      isGDriveSyncMode && portfolioCompanyId
        ? handlePcFolderGoogleSync(documentIds)
        : isGDriveSyncMode
          ? handleAddFolderGoogleSync(documentIds)
          : handleUploadDocsAndRunFieldExtraction(documentIds);
    }
  };

  const getAllChildKeys = (node: GDriveSharedDocumentAPIModel): string[] => {
    return node.children
      ? node.children.flatMap((child) => [child.key, ...getAllChildKeys(child)])
      : [];
  };

  const getDataIds = (
    selectedKeys: Record<string, boolean>,
    nodes: TreeNode[],
    selectOnlyFolders = false,
  ): string[] => {
    const ids: string[] = [];
    const traverse = (node: TreeNode) => {
      if (
        node.key &&
        selectedKeys[node.key] &&
        node.data.mimeType ===
          (selectOnlyFolders
            ? 'application/vnd.google-apps.folder'
            : 'application/pdf')
      ) {
        ids.push(node.data.id);
      }
      if (node.children) {
        node.children.forEach(traverse);
      }
    };

    nodes.forEach(traverse);
    return ids;
  };
  const getParentNode = (
    key: string,
    nodes: TreeNode[],
    parent: TreeNode | null = null,
  ): TreeNode | null => {
    for (const node of nodes) {
      if (node.key === key) {
        return parent || {};
      }
      if (node.children) {
        const found = getParentNode(key, node.children, node);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const handleNodeSelect = (node: TreeNode) => {
    const newSelection =
      selectedKeys && typeof selectedKeys === 'object'
        ? { ...selectedKeys }
        : {};
    const newDocumentIds = [...documentIds];
    if (node) {
      const childKeys = getAllChildKeys(node as GDriveSharedDocumentAPIModel);
      if (node.key && newSelection[node.key]) {
        delete newSelection[node.key];
        childKeys.forEach((key) => delete newSelection[key]);
        if (node.data && node.data.mimeType === 'application/pdf') {
          const index = newDocumentIds.indexOf(node.data.id);
          if (index > -1) {
            newDocumentIds.splice(index, 1);
          }
        }
      } else {
        if (node.key) {
          newSelection[node.key] = true;
        }
        childKeys.forEach((key) => (newSelection[key] = true));

        if (node.data && node.data.mimeType === 'application/pdf') {
          newDocumentIds.push(node.data.id);
        }
      }

      const checkAndSelectParent = (nodeKey: string) => {
        const parentNode = getNodeByKey(nodeKey);
        if (parentNode && parentNode.children) {
          const allChildrenSelected = parentNode.children.every(
            (child) => newSelection[child.key],
          );
          if (allChildrenSelected) {
            newSelection[parentNode.key] = true;
          } else {
            delete newSelection[parentNode.key];
          }
        }
      };

      const checkParentNodes = (nodeKey: string) => {
        const parentNode = getParentNode(nodeKey, sharedFiles);
        if (parentNode) {
          if (typeof parentNode.key === 'string') {
            checkAndSelectParent(parentNode.key);
          }
          if (
            parentNode.key !== node.key &&
            typeof parentNode.key === 'string'
          ) {
            checkParentNodes(parentNode.key);
          }
        }
      };

      if (node && typeof node.key === 'string') {
        checkParentNodes(node.key);
      }
    }
    setSelectedKeys(newSelection);
    setDocumentIds(newDocumentIds);
  };

  const togglerTemplate = (
    node: TreeNode,
    options: TreeTogglerTemplateOptions,
  ) => {
    if (!node) {
      return;
    }
    const expanded = options.expanded;
    const iconClassName = classNames('p-tree-toggler-icon pi pi-fw', {
      'pi-angle-right': !expanded,
      'pi-angle-down': expanded,
    });
    return (
      <button
        type="button"
        className="p-tree-toggler p-link mr-0"
        tabIndex={-1}
        onClick={options.onClick}
      >
        <span className={iconClassName} aria-hidden="true"></span>
      </button>
    );
  };

  const handleFolderMapping = () => {
    toggleFundPcGoogleDriveSync();
  };

  return (
    <div className="flex gap-3 flex-column">
      {!isGDriveSyncMode ? (
        <MessageComponent msgText="To link folder, please share them with Banqora's service account: " />
      ) : null}
      {showPcFolderMapping ? (
        <div
          className="border-round shadow-none p-2 flex justify-content-between"
          style={{
            border: '1px solid lightgrey',
          }}
        >
          <div className="flex align-items-center">
            <Checkbox
              inputId="ingredient1"
              name="pizza"
              value="Cheese"
              checked={folderPcMapping}
              onChange={(e) => setFolderPcMapping(!!e.checked)}
            />
            <label htmlFor="ingredient1" className="ml-2">
              Map Folders Name to Portfolio Companies
            </label>
          </div>
          <>
            <Button
              label="Save"
              size="small"
              loading={folderPcMappingLoading}
              onClick={() => handleFolderMapping()}
            />
          </>
        </div>
      ) : null}
      <Tree
        filter
        filterMode="strict"
        filterPlaceholder="Search..."
        value={sharedFiles}
        selectionMode="multiple"
        selectionKeys={selectedKeys}
        onNodeClick={(e) => handleNodeSelect(e.node as TreeNode)}
        loading={isLoading}
        emptyMessage={'No shared files found.'}
        nodeTemplate={(node) => (
          <>
            <span
              className={`pi pi-${node.data.mimeType === 'application/vnd.google-apps.folder' ? 'folder' : 'file'}
              text-l mr-2 ${node.data.mimeType === 'application/vnd.google-apps.folder' ? 'text-yellow-500' : 'text-blue-500'}`}
            ></span>
            {node.label}
          </>
        )}
        togglerTemplate={togglerTemplate}
        className="w-full"
      />
      <div className="flex justify-content-end">
        <Button
          label={isGDriveSyncMode ? 'Add Sync' : 'Upload'}
          icon={`pi ${isGDriveSyncMode ? 'pi-sync' : 'pi-cloud-upload'}`}
          className="p-button-sm w-7rem"
          loading={submittingForm}
          disabled={!selectedKeys || Object.keys(selectedKeys).length === 0}
          onClick={handleUpload}
        />
      </div>
    </div>
  );
};
